import React, { useState, useEffect } from "react";
import { User } from "@/api/entities";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Bell, BellRing, Info, LogOut } from "lucide-react";
import { motion } from "framer-motion";

// 🔽 utilità push
import { subscribePush, savePushSettings } from "@/lib/push";

export default function Impostazioni() {
  const [user, setUser] = useState(null);
  const [settings, setSettings] = useState({
    notification_checkin_enabled: true,
    notification_checkin_time: "19:00",
    notification_exercises_enabled: true,
    notification_exercise_time: "09:00",
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    const loadUserData = async () => {
      try {
        const currentUser = await User.me();
        setUser(currentUser);
        setSettings({
          notification_checkin_enabled: currentUser.notification_checkin_enabled !== false,
          notification_checkin_time: currentUser.notification_checkin_time || "19:00",
          notification_exercises_enabled: currentUser.notification_exercises_enabled !== false,
          notification_exercise_time: currentUser.notification_exercise_time || "09:00",
        });
      } catch (error) {
        console.error("Errore nel caricare i dati utente:", error);
      } finally {
        setIsLoading(false);
      }
    };
    loadUserData();
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    setSaveSuccess(false);
    try {
      await User.updateMyUserData(settings);

      const me = await User.me();
      try { await subscribePush({ userId: me?.id }); } catch (e) { console.warn("subscribePush fallita:", e); }

      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Rome";
        await savePushSettings({ userId: me?.id, tz, settings });
      } catch (e) { console.warn("savePushSettings fallita:", e); }

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error("Errore nel salvare le impostazioni:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const hourOptions = (start, end) => {
    const arr = [];
    for (let i = start; i <= end; i++) arr.push(String(i).padStart(2, "0"));
    return arr;
  };
  const minuteOptions = () => {
    const arr = [];
    for (let m = 0; m < 60; m++) arr.push(String(m).padStart(2, "0"));
    return arr;
  };
  const parseTime = (hhmm) => {
    const [h = "00", m = "00"] = String(hhmm || "").split(":");
    return { h, m };
  };
  const combineTime = (h, m) => `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const checkParts = parseTime(settings.notification_checkin_time);
  const exerciseParts = parseTime(settings.notification_exercise_time);

  return (
    <div className="p-4 md:p-8">
      <div className="max-w-2xl mx-auto">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Impostazioni</h1>
          <p className="text-lg text-gray-600">Gestisci le tue preferenze e le notifiche.</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <Card className="mt-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><BellRing /> Impostazioni Notifiche</CardTitle>
              <CardDescription>Scegli quando e come vuoi ricevere promemoria.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Check-in */}
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <Label htmlFor="checkin-notifications" className="font-semibold">Promemoria Check-in Giornaliero</Label>
                  <p className="text-sm text-gray-500">Ricevi una notifica la sera se non hai ancora fatto il check-in.</p>
                </div>
                <Switch
                  id="checkin-notifications"
                  checked={settings.notification_checkin_enabled}
                  onCheckedChange={(checked) => setSettings(s => ({ ...s, notification_checkin_enabled: checked }))}
                />
              </div>

              {settings.notification_checkin_enabled && (
                <div className="pl-6 space-y-2">
                  <Label>Orario preferito</Label>
                  <div className="flex items-center gap-2">
                    {/* Ora */}
                    <Select
                      value={checkParts.h}
                      onValueChange={(h) =>
                        setSettings(s => ({ ...s, notification_checkin_time: combineTime(h, parseTime(s.notification_checkin_time).m) }))
                      }
                    >
                      <SelectTrigger className="w-[100px]"><SelectValue placeholder="HH" /></SelectTrigger>
                      <SelectContent>
                        {hourOptions(18, 22).map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <span className="opacity-60">:</span>
                    {/* Minuti */}
                    <Select
                      value={checkParts.m}
                      onValueChange={(m) =>
                        setSettings(s => ({ ...s, notification_checkin_time: combineTime(parseTime(s.notification_checkin_time).h, m) }))
                      }
                    >
                      <SelectTrigger className="w-[100px]"><SelectValue placeholder="MM" /></SelectTrigger>
                      <SelectContent className="max-h-72">
                        {minuteOptions().map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* Esercizi */}
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <Label htmlFor="exercise-notifications" className="font-semibold">Promemoria Esercizi</Label>
                  <p className="text-sm text-gray-500">Ricevi una notifica per fare gli esercizi giornalieri.</p>
                </div>
                <Switch
                  id="exercise-notifications"
                  checked={settings.notification_exercises_enabled}
                  onCheckedChange={(checked) => setSettings(s => ({ ...s, notification_exercises_enabled: checked }))}
                />
              </div>

              {settings.notification_exercises_enabled && (
                <div className="pl-6 space-y-2">
                  <Label>Orario preferito</Label>
                  <div className="flex items-center gap-2">
                    {/* Ora */}
                    <Select
                      value={exerciseParts.h}
                      onValueChange={(h) =>
                        setSettings(s => ({ ...s, notification_exercise_time: combineTime(h, parseTime(s.notification_exercise_time).m) }))
                      }
                    >
                      <SelectTrigger className="w-[100px]"><SelectValue placeholder="HH" /></SelectTrigger>
                      <SelectContent>
                        {hourOptions(7, 21).map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <span className="opacity-60">:</span>
                    {/* Minuti */}
                    <Select
                      value={exerciseParts.m}
                      onValueChange={(m) =>
                        setSettings(s => ({ ...s, notification_exercise_time: combineTime(parseTime(s.notification_exercise_time).h, m) }))
                      }
                    >
                      <SelectTrigger className="w-[100px]"><SelectValue placeholder="MM" /></SelectTrigger>
                      <SelectContent className="max-h-72">
                        {minuteOptions().map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              <Button onClick={handleSave} disabled={isSaving} className="w-full">
                {isSaving ? "Salvataggio..." : "Salva Impostazioni"}
              </Button>
              {saveSuccess && <p className="text-sm text-green-600 text-center">Impostazioni salvate con successo!</p>}
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
          <Card className="mt-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">Gestione Account</CardTitle>
            </CardHeader>
            <CardContent>
              <Button variant="outline" onClick={async () => { try { await User.logout(); window.location.reload(); } catch(e){ console.error(e); }}} className="w-full">
                <LogOut className="w-4 h-4 mr-2" />
                Esci (Logout)
              </Button>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}>
          <Alert className="mt-8 bg-blue-50 border-blue-200">
            <Info className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-800">
              Le notifiche push richiedono l'attivazione delle funzioni backend. Assicurati che la subscription sia attiva e che il cron chiami <code>/api/push-run</code>.
            </AlertDescription>
          </Alert>
        </motion.div>
      </div>
    </div>
  );
}
